import { createFgRuntime, disposeFlowGraph, startFlowGraphs, pumpFlowGraphTick, advanceFlowGraphTasks } from './runtime.js';
import { pumpFgEvent, createFgEventBus, clearFgEventBus, flushFgEvents } from './event-bus.js';
import { FgEventType } from './types.js';

function attachFlowGraph(scene, rt) {
  let list = scene._flowGraphs;
  if (!list) {
    list = [];
    scene._flowGraphs = list;
  }
  list.push(rt);
  ensureFlowGraphCoordinator(scene);
  scene._flowGraphPointerRefresh?.();
}
function detachFlowGraph(scene, rt) {
  const list = scene._flowGraphs;
  if (list) {
    const i = list.indexOf(rt);
    if (i >= 0) {
      list.splice(i, 1);
    }
  }
  disposeFlowGraph(rt);
  scene._flowGraphPointerRefresh?.();
  if (list?.length === 0) {
    removeFlowGraphCoordinator(scene);
  }
}
function ensureFlowGraphCoordinator(scene) {
  if (scene._flowGraphTick) {
    return;
  }
  const tick = (deltaMs) => {
    const runtimes = scene._flowGraphs?.slice() ?? [];
    const eventBuses = new Set(runtimes.map((runtime) => runtime.env.events));
    eventBuses.forEach(flushFgEvents);
    const isAttached = (runtime) => scene._flowGraphs?.includes(runtime) ?? false;
    startFlowGraphs(runtimes, isAttached);
    for (const runtime of runtimes) {
      if (isAttached(runtime)) {
        pumpFlowGraphTick(runtime, deltaMs);
      }
    }
    for (const runtime of runtimes) {
      if (isAttached(runtime)) {
        advanceFlowGraphTasks(runtime, deltaMs);
      }
    }
  };
  const dispose = () => {
    const runtimes = scene._flowGraphs?.splice(0) ?? [];
    runtimes.forEach(disposeFlowGraph);
    scene._flowGraphPointerCleanup?.();
    removeFlowGraphCoordinator(scene);
  };
  scene._flowGraphTick = tick;
  scene._flowGraphDispose = dispose;
  scene._beforeRender.unshift(tick);
  scene._disposables.push(dispose);
}
function removeFlowGraphCoordinator(scene) {
  const tick = scene._flowGraphTick;
  if (tick) {
    const index = scene._beforeRender.indexOf(tick);
    if (index >= 0) {
      scene._beforeRender.splice(index, 1);
    }
  }
  const dispose = scene._flowGraphDispose;
  if (dispose) {
    const index = scene._disposables.indexOf(dispose);
    if (index >= 0) {
      scene._disposables.splice(index, 1);
    }
  }
  scene._flowGraphTick = void 0;
  scene._flowGraphDispose = void 0;
  if (scene._flowGraphBus) {
    clearFgEventBus(scene._flowGraphBus);
    scene._flowGraphBus = void 0;
  }
}
function flowGraphRuntimes(scene) {
  return scene._flowGraphs ?? [];
}
function flowGraphBus(scene) {
  let bus = scene._flowGraphBus;
  if (!bus) {
    bus = createFgEventBus();
    scene._flowGraphBus = bus;
  }
  return bus;
}
async function addFlowGraph(scene, graph, wiring = {}, opts) {
  const rt = await createFgRuntime(
    graph,
    {
      caps: sceneAnimationCaps(),
      ...wiring,
      events: wiring.events ?? flowGraphBus(scene)
    },
    opts
  );
  attachFlowGraph(scene, rt);
  return rt;
}
function dispatchFlowGraphEvent(scene, eventId, values = {}) {
  pumpFgEvent(flowGraphBus(scene), FgEventType.CustomEvent, { eventName: eventId, values });
}
function sceneAnimationCaps() {
  return {
    playAnimation: (group, opts) => {
      group.speedRatio = opts?.speed ?? 1;
      group.loopAnimation = opts?.loop ?? false;
      group.isPlaying = true;
      group._stopped = false;
    },
    stopAnimation: (group) => {
      group.isPlaying = false;
      group.currentTime = 0;
      group._stopped = true;
    },
    stopAnimationAt: (group, frame) => {
      const ctrl = group._ctrl;
      group.currentTime = frame / (group.frameRate || 60);
      group.isPlaying = false;
      if (ctrl) {
        ctrl.time = group.currentTime;
        ctrl.playing = false;
        ctrl.speedRatio = group.speedRatio;
        ctrl.loop = group.loopAnimation;
        ctrl._setMask?.(group.mask ?? null);
        if (!group._stopped || !group._gltfMixer) {
          ctrl.tick(0);
          group.currentTime = ctrl.time;
        }
      }
    }
  };
}
async function runFlowGraphs(scene, loaded, animations = []) {
  const caps = sceneAnimationCaps();
  const events = flowGraphBus(scene);
  const runtimes = [];
  try {
    for (const lg of loaded) {
      const resolveAccessor = lg.resolveAccessor ? (pointer) => lg.resolveAccessor(pointer, scene, animations) : void 0;
      const rt = await createFgRuntime(
        lg.graph,
        { accessors: { ...lg.accessors }, resolveAccessor, animations, caps, events, _assetScope: lg._assetScope },
        { rightHanded: lg.rightHanded ?? true }
      );
      attachFlowGraph(scene, rt);
      runtimes.push(rt);
    }
    return runtimes;
  } catch (error) {
    for (let i = runtimes.length - 1; i >= 0; i--) {
      detachFlowGraph(scene, runtimes[i]);
    }
    throw error;
  }
}

export { addFlowGraph, attachFlowGraph, detachFlowGraph, dispatchFlowGraphEvent, flowGraphBus, flowGraphRuntimes, runFlowGraphs };
//# sourceMappingURL=scene-flow-graph.js.map
